import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Typography,
} from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import DeleteIcon from '@mui/icons-material/Delete';
import { sharedColors } from '../../utils/Style';
import { FilterKey } from '../../interfaces/Filter/FilterKey';
import { getFiltersByCategory } from '../../api/Filter';
import { useLocalizedText } from '../../hooks/useLocalizedText';

type FilterAccordionProps = {
  filterKey: FilterKey;
  filterIDs: number[];
  onToggle: (filterID: number) => void;
};

const FilterAccordion = ({
  filterKey,
  filterIDs,
  onToggle,
}: FilterAccordionProps) => {
  const [open, setOpen] = useState(false);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        boxSizing: 'border-box',
        p: 0.75,
        border: '1px solid #e1e1e1',
      }}
    >
      <Box
        onClick={() => setOpen((x) => !x)}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
        }}
      >
        <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
          {filterKey.title}
        </Typography>

        <IconButton
          sx={{ transform: open ? 'rotate(180deg)' : undefined, p: 0 }}
        >
          <ExpandMoreIcon />
        </IconButton>
      </Box>

      {open && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            px: 1.5,
            gap: 1,
            mt: 0.5,
          }}
        >
          {filterKey.values.map((value) => (
            <FormControlLabel
              key={value.id}
              control={
                <Checkbox
                  size='small'
                  checked={filterIDs.includes(value.id)}
                  onChange={() => onToggle(value.id)}
                  sx={{ p: 0, mr: 0.5 }}
                />
              }
              label={
                <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>
                  {value.title}
                </Typography>
              }
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

type FilterMenuMobileProps = {
  categorySlug: string;
  filterIDs: number[];
  onChange: (newFilterIDs: number[]) => void;
  onClose: () => void;
};

export const FilterMenuMobile = ({
  categorySlug,
  filterIDs,
  onChange,
  onClose,
}: FilterMenuMobileProps) => {
  const t = useLocalizedText();

  const [filters, setFilters] = useState<FilterKey[]>([]);

  useEffect(() => {
    getFiltersByCategory(categorySlug)
      .then(setFilters)
      .catch((err) => toast.error(err.message));
  }, []);

  const onToggle = useCallback(
    (filterID: number) => {
      onChange(
        filterIDs.includes(filterID)
          ? filterIDs.filter((x) => x !== filterID)
          : [...filterIDs, filterID],
      );
    },
    [filterIDs, onChange],
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        alignItems: 'center',
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 1.5,
          width: '100%',
          justifyContent: 'center',
        }}
      >
        <Button
          variant='outlined'
          startIcon={<DoneIcon />}
          onClick={onClose}
          sx={{
            backgroundColor: sharedColors.gray2,
            color: sharedColors.gray7,
            border: '1px solid #d1d1d1',
          }}
        >
          {t('common.confirm')}
        </Button>

        {!!filterIDs.length && (
          <Button
            variant='outlined'
            startIcon={<DeleteIcon />}
            onClick={() => onChange([])}
            sx={{
              backgroundColor: sharedColors.gray2,
              color: sharedColors.gray7,
              border: '1px solid #d1d1d1',
            }}
          >
            {t('common.clear')}
          </Button>
        )}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        {filters.map((filterKey) => (
          <FilterAccordion
            filterKey={filterKey}
            filterIDs={filterIDs}
            onToggle={onToggle}
          />
        ))}
      </Box>
    </Box>
  );
};
